import { useId, useState } from "react";

import { Typography } from "@ui/components/Typography";
import { useTranslations } from "@ui/providers/useTranslations";

import { RadioButton } from "@jti/core-components";
import type { OtpChannel } from "@jti/fe-common";

import { createOtpChannelOptions } from "../../utils/createOtpChannelOptions";
import { getIsOtpChannel } from "../../utils/getIsOtpChannel";

import styles from "./OtpChannelSelector.module.scss";

type OtpChannelSelectorProps = {
  defaultChannel?: OtpChannel;
  isDisabled?: boolean;
  otpOptions?: {
    whatsapp?: boolean;
    viber?: boolean;
  };
  onChannelChange: (channel: OtpChannel) => void;
};

export const OtpChannelSelector = ({
  defaultChannel = "sms",
  isDisabled = false,
  otpOptions = {},
  onChannelChange,
}: OtpChannelSelectorProps) => {
  const { __ } = useTranslations();
  const id = useId();
  const titleId = `otp-channel-title-${id}`;
  const radioGroupName = `otpChannelOptions-${id}`;
  const [otpChannel, setOtpChannel] = useState<string>(defaultChannel);

  const handleChannelChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    setOtpChannel(value);
    if (!getIsOtpChannel(value)) return;
    onChannelChange(value);
  };

  const radioOptions = createOtpChannelOptions(otpOptions).map(({ value, label }) => ({
    value,
    label: (
      <Typography variant="body2" weight="light" className={styles.text}>
        {__(label)}
      </Typography>
    ),
  }));

  return (
    <div className={styles.otpChannelSelector}>
      <div id={titleId}>
        <Typography variant="h5" className={styles.text}>
          {__("phone.otp.channel.title")}
        </Typography>
      </div>

      <fieldset className={styles.options} aria-labelledby={titleId}>
        <div className={styles.optionsList}>
          {radioOptions.map((opt) => (
            <div key={opt.value} className={styles.option}>
              <RadioButton
                name={radioGroupName}
                value={opt.value}
                label={opt.label}
                checked={otpChannel === opt.value}
                onChange={handleChannelChange}
                disabled={isDisabled}
              />
            </div>
          ))}
        </div>
      </fieldset>
    </div>
  );
};
